/**
 * Generic registry for "don't reload out from under me right now" signals
 * that don't fit the more specific registries (widget config, optimistic
 * orders, RFQ — see health-signals.ts's blockersFromUserWork). A source is
 * asked live rather than polled, so a component only has to answer honestly
 * whenever asked instead of pushing updates on every state change.
 *
 * Used by flows that hold real in-progress state with no server-side draft
 * to resume from: an unsubmitted order ticket (react-hook-form `isDirty`),
 * a wallet-onboarding wizard mid-flow. Losing either to an unannounced
 * Level 3 reload is a silent data-loss regression, not just a stale UI.
 */

export type ReloadBlockerSource = {
  /** Stable label surfaced in reloadBlockers/telemetry, e.g. 'order-ticket-dirty'. */
  reason: string
  isBlocking: () => boolean
}

const sources = new Set<ReloadBlockerSource>()

/** Registers one live source. Returns an unregister function. */
export function registerReloadBlockerSource(source: ReloadBlockerSource): () => void {
  sources.add(source)
  return () => {
    sources.delete(source)
  }
}

/** Reasons for every currently-blocking source, deduplicated. */
export function collectActiveReloadBlockerReasons(): string[] {
  const reasons = new Set<string>()
  for (const source of sources) {
    if (source.isBlocking()) reasons.add(source.reason)
  }
  return Array.from(reasons)
}

/** Test seam. */
export function resetReloadBlockerSourcesForTest(): void {
  sources.clear()
}
